import { DataTable } from "@/components/Base/Datatable";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { useCardsListQuery } from "@/hooks/queries/useCardsListQuery";
import {
  Card,
  IconNames,
  Employee,
  LOCAL_STORAGE_BASE_KEYS,
  TerminologyOptions,
} from "@/types/v2";
import { cardsTableColumns } from "./CardsTable.columns";
import { Input } from "@/components/Base/Input";
import { Button } from "@/components/Base/Button";
import { Icon } from "@/components/Common/Icon";
import { cn } from "@/lib/utils";
import { useUserColumns } from "@/hooks/useUserColumns";
import { useTableState } from "@/hooks/useTableState";
import { generateCardDetailsUrl, isValidCardId } from "@/lib/cards";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";

interface CardsTableProps {
  shopId: string;
  employees: Employee[];
}

const layoutDefaults = {
  pagination: { pageSize: 20 },
  sorting: [{ id: "id", desc: true }],
  columnFilters: [],
  columnVisibility: { id: false },
};

export const CardsTable = ({ shopId, employees }: CardsTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { terminolize } = useTerminologyReplacer();
  const navigate = useNavigate();

  const { defaultState, saveTableLayout, resetTableLayout } = useUserColumns(
    LOCAL_STORAGE_BASE_KEYS.CARDS_TABLE_COLUMNS,
    layoutDefaults
  );

  const { savedState, saveTableState } = useTableState(
    LOCAL_STORAGE_BASE_KEYS.CARDS_TABLE_STATE,
    {
      sorting: [{ id: "id", desc: true }],
      filtering: {},
      pagination: { pageIndex: 0, pageSize: 20 },
    }
  );

  // Get values from URL params, falling back to saved state, then defaults
  const currentPage = parseInt(
    searchParams.get("page") ||
      `${(savedState.pagination?.pageIndex ?? 0) + 1}`,
    10
  );
  const pageSize = parseInt(
    searchParams.get("pageSize") ||
      `${savedState.pagination?.pageSize ?? defaultState.pagination?.pageSize ?? 20}`,
    10
  );
  const sortBy =
    searchParams.get("sortBy") ?? savedState.sorting?.[0]?.id ?? "id";
  const sortOrder =
    (searchParams.get("sortOrder") as "asc" | "desc") ??
    (savedState.sorting?.[0]?.desc ? "desc" : "asc");
  const cardNumberFilter =
    searchParams.get("cardNumber") ||
    savedState.filtering?.cardNumber ||
    undefined;

  const { data, isLoading } = useCardsListQuery({
    shopId,
    currentPage,
    pageSize,
    sortBy,
    sortOrder,
    cardNumberFilter,
  });

  // Initialize state from URL params
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: currentPage - 1,
    pageSize: pageSize,
  });

  const [sorting, setSorting] = useState<SortingState>(() => {
    if (sortBy && sortOrder) {
      return [{ id: sortBy, desc: sortOrder === "desc" }];
    }
    return [];
  });

  const [filtering, setFiltering] = useState<{
    [key: string]: string | undefined;
  }>({
    cardNumber: cardNumberFilter || undefined,
  });

  // Update pagination state when URL changes (browser back/forward)
  useEffect(() => {
    const newPageIndex = currentPage - 1;
    const newPageSize = pageSize;

    if (
      pagination.pageIndex !== newPageIndex ||
      pagination.pageSize !== newPageSize
    ) {
      setPagination({
        pageIndex: newPageIndex,
        pageSize: newPageSize,
      });
    }
  }, [currentPage, pageSize]);

  // Update sorting state when URL changes
  useEffect(() => {
    const newSorting =
      sortBy && sortOrder ? [{ id: sortBy, desc: sortOrder === "desc" }] : [];

    const sortingChanged =
      sorting.length !== newSorting.length ||
      (newSorting.length > 0 &&
        (sorting.length === 0 ||
          sorting[0].id !== newSorting[0].id ||
          sorting[0].desc !== newSorting[0].desc));

    if (sortingChanged) {
      setSorting(newSorting);
    }
  }, [sortBy, sortOrder]);

  // Update filtering state when URL changes
  useEffect(() => {
    const newCardNumber = cardNumberFilter || undefined;
    const newFiltering = {
      cardNumber: newCardNumber,
    };

    const filteringChanged = filtering.cardNumber !== newFiltering.cardNumber;

    if (filteringChanged) {
      setFiltering(newFiltering);
    }
  }, [cardNumberFilter]);

  // Update URL when pagination changes and save to localStorage automatically
  useEffect(() => {
    const urlPage = currentPage;
    const urlPageSize = parseInt(searchParams.get("pageSize") || "20", 10);
    const actualPage = pagination.pageIndex + 1;

    if (actualPage !== urlPage || pagination.pageSize !== urlPageSize) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", actualPage.toString());
      newParams.set("pageSize", pagination.pageSize.toString());
      setSearchParams(newParams);
    }

    saveTableState({
      sorting: sorting,
      filtering: filtering,
      pagination: pagination,
    });
  }, [pagination.pageIndex, pagination.pageSize]);

  // Handle sorting changes and reset pagination
  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState)
  ) => {
    const newSorting =
      typeof updaterOrValue === "function"
        ? updaterOrValue(sorting)
        : updaterOrValue;

    setSorting(newSorting);

    saveTableState({
      sorting: newSorting,
      filtering: filtering,
      pagination: pagination,
    });

    const newParams = new URLSearchParams(searchParams);

    if (newSorting.length > 0) {
      const sort = newSorting[0];
      newParams.set("sortBy", sort.id);
      newParams.set("sortOrder", sort.desc ? "desc" : "asc");
    } else {
      newParams.delete("sortBy");
      newParams.delete("sortOrder");
    }

    newParams.set("page", "1");
    setSearchParams(newParams);

    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // Handle filtering changes and reset pagination
  const handleFilteringChange = (
    updaterOrValue:
      | { [key: string]: string | undefined }
      | ((old: { [key: string]: string | undefined }) => {
          [key: string]: string | undefined;
        })
  ) => {
    const newFiltering =
      typeof updaterOrValue === "function"
        ? updaterOrValue(filtering)
        : updaterOrValue;

    setFiltering(newFiltering);

    saveTableState({
      sorting: sorting,
      filtering: newFiltering,
      pagination: pagination,
    });

    const newParams = new URLSearchParams(searchParams);

    if (newFiltering.cardNumber) {
      newParams.set("cardNumber", newFiltering.cardNumber);
    } else {
      newParams.delete("cardNumber");
    }

    newParams.set("page", "1");
    setSearchParams(newParams);

    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const CardNumberFilter = () => {
    const [inputValue, setInputValue] = useState(cardNumberFilter || "");

    const handleApplyFilter = () => {
      handleFilteringChange({
        ...filtering,
        cardNumber: inputValue.trim() || undefined,
      });
    };

    const handleClearFilter = () => {
      setInputValue("");
      handleFilteringChange({
        ...filtering,
        cardNumber: undefined,
      });
    };

    return (
      <div className="flex flex-row items-end">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-200">
            Search{" "}
            {terminolize(
              "Repair Order Number",
              TerminologyOptions.repairOrderNumber,
              "singular"
            )}
          </label>
          <div className="flex flex-row gap-0">
            <Input
              placeholder={`Enter ${terminolize(
                "repair order number",
                TerminologyOptions.repairOrderNumber,
                "singular"
              )}...`}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleApplyFilter();
                }
              }}
              className="w-48 rounded-r-none"
            />
            <button
              onClick={handleApplyFilter}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-r-md border border-l-0 border-muted bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50",
                cardNumberFilter && "rounded-r-none"
              )}
              disabled={inputValue.trim() === (cardNumberFilter || "")}
            >
              <Icon name={IconNames.search} />
            </button>
            {cardNumberFilter && (
              <button
                onClick={handleClearFilter}
                className="flex h-10 w-10 items-center justify-center rounded-r-md border border-l-0 border-muted bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Icon name={IconNames.clear} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const actions = (card: Card) => {
    return [
      <Button
        key="view"
        onClick={(e) => {
          e.stopPropagation(); // Prevent row click when action button is clicked
          navigate(`${card.id}`);
        }}
        variant="ghost-alt"
        size="sm"
      >
        <Icon name={IconNames.open} />
      </Button>,
    ];
  };

  // Handle row click navigation
  const handleRowClick = (card: Card, event: React.MouseEvent) => {
    // Check if the click target is an interactive element
    const target = event.target as HTMLElement;
    const isInteractiveElement = target.closest(
      "button, a, input, select, textarea"
    );

    if (isInteractiveElement) {
      return; // Don't navigate if clicking on an interactive element
    }

    if (!isValidCardId(card.id)) {
      console.error("Invalid card ID:", card.id);
      return;
    }

    const url = generateCardDetailsUrl(shopId, card.id);

    // Handle Ctrl/Cmd+click for new tab
    if (event.ctrlKey || event.metaKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  // Handle keyboard navigation
  const handleRowKeyDown = (card: Card, event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();

      if (!isValidCardId(card.id)) {
        console.error("Invalid card ID:", card.id);
        return;
      }

      const url = generateCardDetailsUrl(shopId, card.id);
      navigate(url);
    }
  };

  return (
    <DataTable
      data={data?.docs ?? []}
      columns={cardsTableColumns({
        actions: actions,
        employees: employees,
        terminolize: terminolize,
      })}
      pagination={pagination}
      setPagination={setPagination}
      sorting={sorting}
      setSorting={handleSortingChange}
      rowCount={data?.total}
      loading={!data || isLoading}
      showRecordRange={true}
      userDefaults={defaultState}
      factoryDefaults={layoutDefaults}
      onLayoutSave={saveTableLayout}
      onLayoutReset={resetTableLayout}
      onRowClick={handleRowClick}
      onRowKeyDown={handleRowKeyDown}
      rowClassName="cursor-pointer hover:bg-muted/50 focus:bg-muted/50"
      headingContent={
        <div className="flex flex-row gap-2 p-2">
          <CardNumberFilter />
        </div>
      }
    />
  );
};
